import { FormikErrors, FormikTouched } from 'formik';
import { NUMBER_OF_STEPS, STEPS } from './constants';
import { LessonForm, SubjectArea } from './types';

export function isStepErrored(
  label: string,
  errors: FormikErrors<LessonForm>,
  touched: FormikTouched<LessonForm>,
): boolean {
  switch (label) {
    // Basic Information
    case STEPS[0].label:
      return (
        (Boolean(touched.basicInfo?.lesson?.title) && Boolean(errors?.basicInfo?.lesson?.title)) ||
        (Boolean(touched.basicInfo?.lesson?.description) &&
          Boolean(errors.basicInfo?.lesson?.description)) ||
        (Boolean(touched.basicInfo?.gradeLevels) && Boolean(errors.basicInfo?.gradeLevels))
      );
    // Technology
    case STEPS[1].label:
      return (
        (Boolean(touched.techRequirements?.technologies) &&
          Boolean(errors?.techRequirements?.technologies)) ||
        (Boolean(touched?.techRequirements?.categories) &&
          Boolean(errors?.techRequirements?.categories)) ||
        (Boolean(touched?.techRequirements?.platforms) &&
          Boolean(errors?.techRequirements?.platforms))
      );
    // Subject Areas
    case STEPS[2].label:
      const subjectAreasErrors = (errors?.subjectAreas as FormikErrors<SubjectArea[]>) ?? [];
      const subjectAreasTouched = (touched?.subjectAreas as FormikTouched<SubjectArea[]>) ?? [];
      return subjectAreasErrors.some(
        (sectionErrors, index) =>
          (Boolean(subjectAreasTouched[index]?.subject) && Boolean(sectionErrors?.subject)) ||
          (Boolean(subjectAreasTouched[index]?.contentStrands) &&
            Boolean(sectionErrors?.contentStrands)) ||
          (Boolean(subjectAreasTouched[index]?.mnStandards) && Boolean(sectionErrors?.mnStandards)),
      );
    // Upload
    case STEPS[3].label:
      return Boolean(touched?.upload && Boolean(errors?.upload));
    // Personal Information
    case STEPS[4].label:
      return (
        (Boolean(touched?.personalInformation?.name) &&
          Boolean(errors?.personalInformation?.name)) ||
        (Boolean(touched?.personalInformation?.email) &&
          Boolean(errors?.personalInformation?.email)) ||
        (Boolean(touched?.personalInformation?.twitter) &&
          Boolean(errors?.personalInformation?.twitter)) ||
        (Boolean(touched?.personalInformation?.school?.name) &&
          Boolean(errors?.personalInformation?.school?.name)) ||
        (Boolean(touched?.personalInformation?.school?.district) &&
          Boolean(errors?.personalInformation?.school?.district)) ||
        (Boolean(touched?.personalInformation?.school?.zip) &&
          Boolean(errors?.personalInformation?.school?.zip)) ||
        (Boolean(touched?.personalInformation?.school?.state) &&
          Boolean(errors?.personalInformation?.school?.state)) ||
        (Boolean(touched?.personalInformation?.displayInfo) &&
          Boolean(errors?.personalInformation?.displayInfo))
      );
    // Permissions
    case STEPS[5].label:
      return (
        (Boolean(touched?.permissions?.permissionGranted) &&
          Boolean(errors?.permissions?.permissionGranted)) ||
        (Boolean(touched?.permissions?.allResourcesLicensed) &&
          Boolean(errors?.permissions?.allResourcesLicensed))
      );
    default:
      return false;
  }
}

export const previousStep = (
  activeStep: number,
  setActiveStep: (step: number) => void,
  values: LessonForm,
  setFieldTouched: (field: string) => void,
) => () => {
  if (activeStep > 0) {
    validateStep(values, setFieldTouched)(activeStep);
    setActiveStep(activeStep - 1);
  }
};

export const nextStep = (
  activeStep: number,
  setActiveStep: (step: number) => void,
  values: LessonForm,
  setFieldTouched: (field: string) => void,
) => () => {
  if (activeStep < NUMBER_OF_STEPS - 1) {
    validateStep(values, setFieldTouched)(activeStep);
    setActiveStep(activeStep + 1);
  }
};

export const goToStep = (
  activeStep: number,
  goToStep: number,
  setActiveStep: (step: number) => void,
  values: LessonForm,
  setFieldTouched: (field: string) => void,
) => {
  validateStep(values, setFieldTouched)(activeStep);
  setActiveStep(goToStep);
};

const validateStep = (values: LessonForm, setFieldTouched: (field: string) => void) => (
  step: number,
) => {
  switch (step) {
    // Basic Information
    case 0:
      setFieldTouched('basicInfo.lesson.title');
      setFieldTouched('basicInfo.lesson.description');
      setFieldTouched('basicInfo.gradeLevels');
      break;
    // Technology
    case 1:
      setFieldTouched('techRequirements.technologies');
      setFieldTouched('techRequirements.categories');
      setFieldTouched('techRequirements.platforms');
      break;
    // Subject Areas
    case 2:
      values.subjectAreas.forEach((_, index) => {
        setFieldTouched(`subjectAreas[${index}].subject`);
        setFieldTouched(`subjectAreas[${index}].contentStrands`);
        setFieldTouched(`subjectAreas[${index}].mnStandards`);
      });
      break;
    // Upload
    case 3:
      setFieldTouched('upload');
      break;
    // Personal Information
    case 4:
      setFieldTouched('personalInformation.name');
      setFieldTouched('personalInformation.email');
      setFieldTouched('personalInformation.twitter');
      setFieldTouched('personalInformation.school.name');
      setFieldTouched('personalInformation.school.district');
      setFieldTouched('personalInformation.school.zip');
      setFieldTouched('personalInformation.school.state');
      setFieldTouched('personalInformation.displayInfo');
      break;
    // Permissions
    case 5:
      setFieldTouched('permissions.allResourcesLicensed');
      setFieldTouched('permissions.permissionGranted');
      break;
  }
};
